import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Paper
} from '@mui/material';
import DashboardLayout from '../components/DashboardLayout';
import { styled } from '@mui/material/styles';
import { getOfferTemplates, getTemplateCategories, createOfferFromTemplate } from '../services/offerTemplateService';
import { useAuth } from '../context/AuthContext';
import TemplateIcon from '@mui/icons-material/ViewModule';
import CreateIcon from '@mui/icons-material/Add';
import CategoryIcon from '@mui/icons-material/Category';
import UsageIcon from '@mui/icons-material/TrendingUp';
import PreviewIcon from '@mui/icons-material/Visibility';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const CategoryChip = styled(Chip)(({ theme }) => ({
  textTransform: 'capitalize',
  fontWeight: 'bold',
}));

const OfferTemplates = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [creatingOffer, setCreatingOffer] = useState(false);

  useEffect(() => {
    fetchTemplates();
    fetchCategories();
  }, [selectedCategory]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const params = selectedCategory ? { category: selectedCategory } : {};
      const data = await getOfferTemplates(params);
      setTemplates(data.templates);
      setError(null);
    } catch (err) {
      setError('Failed to fetch templates. Please try again.');
      console.error('Error fetching templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await getTemplateCategories();
      setCategories(data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
  };

  const handlePreview = (template) => {
    setPreviewTemplate(template);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewTemplate(null);
  };

  const handleUseTemplate = async (template) => {
    if (!isAuthenticated) {
      navigate('/signin');
      return;
    }

    try {
      setCreatingOffer(true);
      // Navigate to template-based offer creation
      navigate(`/offers/new?templateId=${template._id}`);
    } catch (err) {
      setError('Failed to use template. Please try again.');
      console.error('Error using template:', err);
    } finally {
      setCreatingOffer(false);
    }
  };

  const formatPrice = (price) => {
    return price ? `$${price.toFixed(2)}` : '';
  };

  const calculateSavings = (original, final) => {
    if (!original || !final) return '';
    const savings = original - final;
    const percentage = ((savings / original) * 100).toFixed(0);
    return `Save ${percentage}% (${formatPrice(savings)})`;
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  const templatesActions = isAuthenticated ? (
    <Button
      variant="outlined"
      startIcon={<CreateIcon />}
      onClick={() => navigate('/templates/new')}
    >
      Create Template
    </Button>
  ) : null;

  return (
    <DashboardLayout title="Offer Templates" actions={templatesActions} maxWidth="lg">
      <Box sx={{ maxWidth: 'lg', mx: 'auto', px: 3, py: 2 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Choose from our collection of professionally designed offer templates to quickly create compelling deals.
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Filter by Category</InputLabel>
              <Select
                value={selectedCategory}
                label="Filter by Category"
                onChange={handleCategoryChange}
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {templates.length === 0 && !loading ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No templates found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {selectedCategory 
              ? `No templates available in the "${selectedCategory}" category.`
              : 'No templates are currently available.'
            }
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {templates.map((template) => (
            <Grid item xs={12} sm={6} md={4} key={template._id}>
              <StyledCard>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" component="h3" gutterBottom>
                      {template.name}
                    </Typography>
                    <CategoryChip 
                      label={template.category} 
                      size="small" 
                      icon={<CategoryIcon />}
                    />
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {template.description}
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Sample Offer:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {template.template.title}
                    </Typography>
                    
                    {(template.template.originalPrice || template.template.finalPrice) && (
                      <Box sx={{ mb: 1 }}>
                        {template.template.originalPrice && (
                          <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                            {formatPrice(template.template.originalPrice)}
                          </Typography>
                        )}
                        {template.template.finalPrice && (
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            {formatPrice(template.template.finalPrice)}
                          </Typography>
                        )}
                        {template.template.originalPrice && template.template.finalPrice && (
                          <Typography variant="caption" sx={{ color: 'success.main' }}>
                            {calculateSavings(template.template.originalPrice, template.template.finalPrice)}
                          </Typography>
                        )}
                      </Box>
                    )}
                    
                    {template.template.discountPercentage && (
                      <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                        {template.template.discountPercentage}% OFF
                      </Typography>
                    )}
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <UsageIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                      Used {template.usageCount} times
                    </Typography>
                  </Box>
                  
                  {template.template.tags && template.template.tags.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {template.template.tags.slice(0, 3).map((tag, index) => (
                        <Chip key={index} label={tag} size="small" variant="outlined" />
                      ))}
                      {template.template.tags.length > 3 && (
                        <Chip label={`+${template.template.tags.length - 3}`} size="small" variant="outlined" />
                      )}
                    </Box>
                  )}
                </CardContent>
                
                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button 
                    size="small" 
                    startIcon={<PreviewIcon />}
                    onClick={() => handlePreview(template)}
                  >
                    Preview
                  </Button>
                  <Button 
                    variant="contained" 
                    size="small" 
                    startIcon={<CreateIcon />}
                    onClick={() => handleUseTemplate(template)}
                    disabled={creatingOffer}
                    sx={{ ml: 'auto' }}
                  >
                    Use Template
                  </Button>
                </CardActions>
              </StyledCard>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Template Preview Dialog */}
      <Dialog 
        open={previewOpen} 
        onClose={handleClosePreview}
        maxWidth="md" 
        fullWidth
      >
        {previewTemplate && (
          <>
            <DialogTitle>
              Template Preview: {previewTemplate.name}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 2 }}>
                <CategoryChip 
                  label={previewTemplate.category} 
                  icon={<CategoryIcon />}
                  sx={{ mb: 2 }}
                />
                <Typography variant="body1" sx={{ mb: 3 }}>
                  {previewTemplate.description}
                </Typography>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" gutterBottom>
                Sample Offer Details:
              </Typography>
              
              <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {previewTemplate.template.title}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {previewTemplate.template.description}
                </Typography>
                
                {(previewTemplate.template.originalPrice || previewTemplate.template.finalPrice) && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ color: '#FFFFFF' }}>
                      Pricing:
                    </Typography>
                    {previewTemplate.template.originalPrice && (
                      <Typography variant="body1" sx={{ textDecoration: 'line-through' }}>
                        Original: {formatPrice(previewTemplate.template.originalPrice)}
                      </Typography>
                    )}
                    {previewTemplate.template.finalPrice && (
                      <Typography variant="h6" sx={{ color: 'success.main' }}>
                        Now: {formatPrice(previewTemplate.template.finalPrice)}
                      </Typography>
                    )}
                  </Box>
                )}
                
                {previewTemplate.template.features && previewTemplate.template.features.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Features:
                    </Typography>
                    <ul>
                      {previewTemplate.template.features.map((feature, index) => (
                        <li key={index}>{feature}</li>
                      ))}
                    </ul>
                  </Box>
                )}
                
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Chip 
                    label={previewTemplate.template.urgencyText} 
                    color="warning" 
                    size="small" 
                  />
                  <Chip 
                    label={`Valid for ${previewTemplate.template.validityDays} days`} 
                    variant="outlined" 
                    size="small" 
                  />
                </Box>
                
                <Button variant="contained" size="large" sx={{ mt: 2 }}>
                  {previewTemplate.template.callToAction}
                </Button>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClosePreview}>
                Close
              </Button>
              <Button 
                variant="contained" 
                onClick={() => {
                  handleClosePreview();
                  handleUseTemplate(previewTemplate);
                }}
                startIcon={<CreateIcon />}
              >
                Use This Template
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
    </DashboardLayout>
  );
};

export default OfferTemplates; 